import PropTypes from 'prop-types';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const CardContext = createContext();

export const CardProvider = ({ children }) => {
    const [cardData, setCardData] = useState({});
    const isMounted = useRef(true);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    /**
     * Updates card data from sensor readings
     * Only updates the local UI state without persistence to backend
     */
    const updateCardData = (macAddress, data) => {
        if (!isMounted.current || !macAddress) return;
        
        console.log('CardContext updateCardData called:', {
            macAddress,
            data,
            isMounted: isMounted.current
        });
        
        // Map incoming data to our internal format (distancetwo = second water level / irrigation2 — see mqttUtils ambient payload)
        // mostRecentEpochMs is the wall-clock time (ms) this live packet was received. It is the
        // authoritative freshness signal for online/offline + "last seen" — do NOT derive freshness
        // from the formatted display timestamp (that is a locale/timezone string, not a clock).
        const updatedData = {
            mostRecentTemp: data.temperature,
            mostRecentHumidity: data.humidity,
            mostRecentMoisture: data.moisture,
            mostRecentDistance: data.distance,
            mostRecentDistance2: data.distancetwo,
            mostRecentLight: data.light,
            mostRecentTimestamp: data.timestamp,
            mostRecentEpochMs: Date.now()
        };
        
        // Update local state only - no backend persistence for MQTT data
        setCardData(prevData => {
            const newData = {
                ...prevData,
                [macAddress]: {
                    ...prevData[macAddress],
                    ...updatedData
                }
            };
            console.log('CardContext state updated:', {
                macAddress,
                newData: newData[macAddress]
            });
            return newData;
        });
    }

    /**
     * Initializes card data from backend sensor values
     * Used to populate CardContext with last known values when card is first rendered
     */
    const initializeCardData = (macAddress, sensorData) => {
        if (!isMounted.current || !macAddress || !sensorData) return;
        
        // Only initialize if we don't already have data for this MAC address
        setCardData(prevData => {
            if (prevData[macAddress]) {
                // Already have data, don't overwrite
                return prevData;
            }
            
            // Initialize with sensor data from backend
            const initializedData = {
                mostRecentTemp: sensorData.mostRecentTemp,
                mostRecentHumidity: sensorData.mostRecentHumidity,
                mostRecentMoisture: sensorData.mostRecentMoisture,
                mostRecentDistance: sensorData.mostRecentDistance,
                mostRecentDistance2: sensorData.mostRecentDistance2,
                mostRecentLight: sensorData.mostRecentLight,
                mostRecentTimestamp: sensorData.mostRecentTimestamp
            };
            
            console.log('CardContext initializeCardData called:', {
                macAddress,
                initializedData
            });
            
            return {
                ...prevData,
                [macAddress]: initializedData
            };
        });
    }

    const value = {
        cardData,
        updateCardData,
        initializeCardData
    };

    return (
        <CardContext.Provider value={value}>
            {children}
        </CardContext.Provider>
    )
}

export const useCardData = () => {
    const context = useContext(CardContext);
    if (!context) {
        throw new Error('useCardData must be used within a CardProvider');
    }
    return context;
}

// Alias for consistent naming with newer components
export const useCardContext = useCardData;
CardProvider.propTypes = {
    children: PropTypes.node
}
